// apps/client/src/hooks/useDashboardQuery.ts
// Shared plumbing for every dashboard/report data hook (Tier 3 loading UX).
//
// Wraps a Firebase callable in @tanstack/react-query with per-tenant cache
// semantics controlled by `companies/{id}.featureFlags.dashboard_query_cache`
// (tri-state, house rollout convention; fail-safe default 'legacy'):
//
//   'legacy' (default): staleTime 0 and loading = isFetching. Nothing is ever
//     served from cache; every mount/param change/refetch reports loading,
//     exactly like the bespoke useState/useEffect hooks this replaces. In-flight
//     dedup of identical concurrent requests still applies (pure saving, no
//     staleness). gcTime is a small 60s (LEGACY_GC_TIME_MS), NOT 0. See below.
//
//   Why legacy gcTime is 60s, not 0 (2026-07-20): a page can mount two
//     simultaneous queries of the SAME callable (DashboardPage fires
//     getRolling3Run for the current AND prior-year windows at once). With
//     gcTime 0, the instant a query has no observer its settled result is
//     discarded; staleTime 0 then means the sibling/next mount has nothing to
//     reuse and, if its own refetch is dropped, re-zeros an already-rendered
//     headline (the prior-year YoY badges rendered "No prior year data").
//     Keeping settled results for 60s bridges that window WITHOUT changing
//     legacy freshness: staleTime is still 0, so every reuse triggers a fresh
//     background fetch, and the retained entry only prevents a just-settled
//     result from vanishing while a sibling query is in flight. Bug plus
//     empirical proof (flipping to 'observe' restored the badges): this PR.
//   'observe' — soak rung: cache entries are kept (gcTime 30m) and the
//     QueryCacheLifecycle rules run, but staleTime stays 0 so nothing stale is
//     ever SERVED — user-visible behavior stays legacy while the cache layer,
//     invalidation events, and memory profile prove out in production.
//   'swr' — stale-while-revalidate: revisited dashboards paint instantly from
//     cache (loading = isLoading) while a background refetch runs.
//
// Freshness bounds in 'swr' mode:
//   - Upload/aggregation changes: QueryCacheLifecycle hard-resets all
//     dashboard queries when an aggregation run completes, so post-upload
//     figures can never be displayed stale (active queries drop to their
//     loading state and refetch).
//   - Out-of-band mutations (admin-portal config edits such as deduction
//     treatment lists, backend changes) have no client-side event; their
//     staleness is bounded by SWR_STALE_TIME_MS (5 minutes). This is the
//     accepted trade-off of 'swr' mode — flip the tenant back to 'observe'
//     or 'legacy' if that bound is ever unacceptable.
//
// The signed-in uid is part of every query key as defense-in-depth against
// serving one user's role-scoped data to another on a shared machine — the
// backend scopes responses by auth claims, so identical params do NOT imply
// identical data across users. (QueryCacheLifecycle also clears the cache on
// real user switches; the key prefix makes leakage structurally impossible
// even if that clear were ever missed.)
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useAuth } from './useAuth';
import { useCompany } from '../context/CompanyContext';
import { DASHBOARD_QUERY_PREFIX } from '../lib/queryClient';
import type { DashboardCacheMode } from '../lib/queryClient';

export { DASHBOARD_QUERY_PREFIX } from '../lib/queryClient';
export type { DashboardCacheMode } from '../lib/queryClient';

/** How long cached dashboard data is considered fresh in 'swr' mode. */
const SWR_STALE_TIME_MS = 5 * 60 * 1000;
/** How long unobserved cache entries survive in 'observe'/'swr' modes. */
const CACHE_GC_TIME_MS = 30 * 60 * 1000;
/**
 * Legacy-mode retention: settled results survive 60s after their last observer
 * so two simultaneous windows of the same callable can't discard each other's
 * result. staleTime stays 0, so this changes nothing about legacy freshness;
 * it only stops a just-settled result from being garbage-collected mid-render.
 * See the mode-contract header for the discarded-result bug this fixes.
 */
const LEGACY_GC_TIME_MS = 60 * 1000;

/**
 * Resolve the tenant's cache mode from the company doc already loaded by
 * CompanyProvider (zero extra reads). Fail-safe: anything other than the
 * literal 'observe'/'swr' — including missing flag, missing company, load
 * error — means 'legacy'.
 */
export function useDashboardCacheMode(): DashboardCacheMode {
  const { company } = useCompany();
  const flag = company?.featureFlags?.dashboard_query_cache;
  const mode = flag === 'swr' || flag === 'observe' ? flag : 'legacy';
  // One line per app load (module-level latch) — cache-mode visibility for
  // rollout debugging; greppable as [dashboardCache].
  if (!loggedMode && company) {
    loggedMode = true;
    console.log(`[dashboardCache] mode=${mode} flag=${String(flag)} company=${company.id}`);
  }
  return mode;
}

let loggedMode = false;

interface UseDashboardQueryOptions {
  /** Gate fetching on hook-specific preconditions (default true). */
  enabled?: boolean;
}

interface UseDashboardQueryResult<TResponse> {
  data: TResponse | undefined;
  /**
   * legacy/observe: true whenever a fetch is in flight (bespoke-hook parity —
   * consumers' skeleton gates fire on every fetch). swr: true only while
   * fetching WITHOUT cached data, so cache hits paint instantly.
   */
  loading: boolean;
  error: string | null;
}

/**
 * Fetch a dashboard callable through the shared query cache.
 *
 * `params` must be built EXACTLY as the pre-migration hook built its callable
 * payload (including undefined members) — it is both the request body and the
 * cache identity. react-query's default hash serializes objects with sorted
 * keys and drops undefined values, so key equality matches payload equality.
 */
export function useDashboardQuery<TResponse>(
  callableName: string,
  params: { companyId: string } & Record<string, unknown>,
  options?: UseDashboardQueryOptions
): UseDashboardQueryResult<TResponse> {
  const { user } = useAuth();
  const cacheMode = useDashboardCacheMode();

  const query = useQuery<TResponse>({
    queryKey: [DASHBOARD_QUERY_PREFIX, user?.uid, callableName, params],
    queryFn: async () => {
      const callable = httpsCallable<typeof params, TResponse>(functions, callableName);
      const result = await callable(params);
      return result.data;
    },
    enabled: !!user && !!params.companyId && (options?.enabled ?? true),
    staleTime: cacheMode === 'swr' ? SWR_STALE_TIME_MS : 0,
    gcTime: cacheMode === 'legacy' ? LEGACY_GC_TIME_MS : CACHE_GC_TIME_MS,
  });

  // Preserve the diagnostic surface the bespoke hooks had: every callable
  // failure lands in the console with the callable name (greppable in prod
  // debugging, e.g. via Chrome MCP read_console_messages).
  useEffect(() => {
    if (query.error) {
      console.error(`[useDashboardQuery] ${callableName} failed:`, query.error);
    }
  }, [callableName, query.error]);

  return {
    data: query.data,
    loading: cacheMode === 'swr' ? query.isLoading : query.isFetching,
    error: query.error ? (query.error instanceof Error ? query.error.message : String(query.error)) : null,
  };
}
